import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container, Box, Typography, Avatar, Chip, Divider,
  CircularProgress, Alert, IconButton, Paper, TextField, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import CommentIcon from "@mui/icons-material/Comment";
import MessageIcon from "@mui/icons-material/Message";
import { posts as postsApi, inquiries } from "../api/endpoints";
import type { Post, Comment } from "../types";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { NAVY, CRIMSON, CARD_TINT_GRADIENT } from "../theme/theme";
import { alpha } from "@mui/material/styles";

const POST_TYPE_KEYS: Record<string, string> = {
  general_discussion: "feed.generalDiscussion",
  housing_offer:      "feed.housingOffer",
  housing_request:    "feed.housingRequest",
  job_offer:          "feed.jobOffer",
  job_request:        "feed.jobRequest",
  friend_request:     "feed.friendRequest",
  other:              "feed.other",
};

export default function PostDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user } = useAuth();

  const [post, setPost] = useState<Post | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [commentText, setCommentText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [inquireOpen, setInquireOpen] = useState(false);
  const [inquireMsg, setInquireMsg] = useState("");
  const [inquireLoading, setInquireLoading] = useState(false);
  const [inquireError, setInquireError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    const postId = Number(id);
    setLoading(true);
    Promise.all([
      postsApi.get(postId),
      postsApi.listComments(postId),
    ]).then(([p, c]) => {
      setPost(p ?? null);
      setLikeCount(p?.likes_count ?? 0);
      setComments(c);
    }).catch((err) => {
      setError(err instanceof Error ? err.message : t("feed.postLoadError"));
    }).finally(() => setLoading(false));
  }, [id]);

  const handleLike = async () => {
    if (!post) return;
    try {
      if (liked) {
        await postsApi.unlike(post.id);
        setLiked(false);
        setLikeCount((n) => Math.max(0, n - 1));
      } else {
        await postsApi.like(post.id);
        setLiked(true);
        setLikeCount((n) => n + 1);
      }
    } catch { /* ignore */ }
  };

  const handleInquire = async () => {
    if (!post || !user || !inquireMsg.trim()) return;
    setInquireLoading(true);
    try {
      // No receiver_id: the backend resolves the recipient from the post so an
      // anonymous author's identity is never exposed to the client.
      await inquiries.create({
        entity_type: "post",
        entity_id: post.id,
        message: inquireMsg.trim(),
      });
      setInquireOpen(false);
      setInquireMsg("");
    } catch (err) {
      setInquireError(err instanceof Error ? err.message : t("common.error"));
    }
    finally { setInquireLoading(false); }
  };

  const handleComment = async () => {
    if (!post || !commentText.trim()) return;
    setSubmitting(true);
    try {
      await postsApi.addComment(post.id, { content: commentText.trim() });
      setCommentText("");
      const updated = await postsApi.listComments(post.id);
      setComments(updated);
    } catch { /* ignore */ } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !post) {
    return (
      <Container maxWidth="sm" sx={{ py: 3 }}>
        <Alert severity="error">{error ?? t("feed.postNotFound")}</Alert>
      </Container>
    );
  }

  const creatorName = post.is_anonymous
    ? t("common.anonymous")
    : post.creator
    ? `${post.creator.first_name} ${post.creator.last_name}`
    : t("common.anonymous");

  const creatorInitial = post.is_anonymous ? "?" : (post.creator?.first_name?.charAt(0) ?? "?");

  return (
    <>
    <Box
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1.5, py: 1.5,
        overflow: "hidden",
        color: "white",
        background: `linear-gradient(135deg, ${NAVY} 0%, #1a3a7a 55%, ${alpha(CRIMSON, 0.92)} 100%)`,
        "&::before": {
          content: '""',
          position: "absolute",
          top: -40, right: -20,
          width: 130, height: 130,
          borderRadius: "50%",
          background: alpha("#E8A020", 0.18),
          filter: "blur(30px)",
          pointerEvents: "none",
        },
      }}
    >
      <IconButton onClick={() => navigate(-1)} size="small" sx={{ color: "white", position: "relative", zIndex: 1 }}>
        <ArrowBackIcon />
      </IconButton>
      <Typography variant="subtitle1" fontWeight={700} sx={{ position: "relative", zIndex: 1 }}>
        {t("common.post")}
      </Typography>
    </Box>
    <Container maxWidth="sm" sx={{ py: 2 }}>
      {/* Post card */}
      <Paper elevation={0} sx={{ border: `1px solid ${alpha(NAVY, 0.1)}`, borderRadius: 3, p: 2.5, mb: 2, backgroundImage: CARD_TINT_GRADIENT, backgroundColor: "#FFFFFF" }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.5 }}>
          <Avatar
            sx={{ background: post.is_anonymous ? "#9CA3AF" : "linear-gradient(135deg, #1a3a7a 0%, #0F2156 100%)", width: 44, height: 44, fontWeight: 700, cursor: post.creator && !post.is_anonymous ? "pointer" : "default" }}
            onClick={() => { if (post.creator && !post.is_anonymous) navigate(`/app/profile/${post.creator.username}`); }}
          >
            {creatorInitial}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ cursor: post.creator && !post.is_anonymous ? "pointer" : "default", "&:hover": post.creator && !post.is_anonymous ? { color: CRIMSON } : {} }}
              onClick={() => { if (post.creator && !post.is_anonymous) navigate(`/app/profile/${post.creator.username}`); }}
            >
              {creatorName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {new Date(post.created_at).toLocaleString()}
            </Typography>
          </Box>
          <Chip
            label={t(POST_TYPE_KEYS[post.post_type] ?? "feed.other")}
            size="small"
            sx={{ fontSize: "0.65rem", background: "linear-gradient(135deg, #E8A020 0%, #B9781A 100%)", color: "white", fontWeight: 700 }}
          />
        </Box>

        {/* Content */}
        <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.7, mb: 2 }}>
          {post.content}
        </Typography>

        <Divider sx={{ my: 1.5 }} />

        {/* Likes & comments count row */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            onClick={user ? handleLike : undefined}
            sx={{
              display: "flex", alignItems: "center", gap: 0.6,
              px: 1.4, py: 0.5, borderRadius: 20,
              cursor: user ? "pointer" : "default",
              border: `1.5px solid ${liked ? "transparent" : alpha(CRIMSON, 0.3)}`,
              backgroundImage: liked ? "linear-gradient(135deg, #DC2626 0%, #B91C2C 100%)" : "none",
              bgcolor: liked ? "transparent" : alpha(CRIMSON, 0.06),
              color: liked ? "#fff" : CRIMSON,
              boxShadow: liked ? `0 3px 10px ${alpha(CRIMSON, 0.35)}` : "none",
              transition: "all 0.15s",
              "&:hover": user ? { bgcolor: liked ? undefined : alpha(CRIMSON, 0.12), borderColor: CRIMSON } : {},
            }}
          >
            {liked ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
            <Typography variant="caption" fontWeight={700}>{likeCount}</Typography>
          </Box>
          <Box
            sx={{
              display: "flex", alignItems: "center", gap: 0.6,
              px: 1.4, py: 0.5, borderRadius: 20,
              backgroundImage: "linear-gradient(135deg, #1a3a7a 0%, #0F2156 100%)",
              color: "#fff",
              boxShadow: `0 3px 10px ${alpha(NAVY, 0.3)}`,
            }}
          >
            <CommentIcon fontSize="small" />
            <Typography variant="caption" fontWeight={700}>{comments.length}</Typography>
          </Box>
          {user && post.creator_id !== user.id && (
            <Box
              onClick={() => { setInquireError(null); setInquireOpen(true); }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.6,
                ml: "auto",
                px: 1.4,
                py: 0.5,
                borderRadius: 20,
                cursor: "pointer",
                border: `1.5px solid ${alpha("#B9781A", 0.35)}`,
                bgcolor: alpha("#E8A020", 0.1),
                color: "#8A5D14",
                transition: "all 0.15s",
                "&:hover": {
                  backgroundImage: "linear-gradient(135deg, #E8A020 0%, #B9781A 100%)",
                  bgcolor: "transparent",
                  color: "#fff",
                  borderColor: "transparent",
                  boxShadow: `0 3px 10px ${alpha("#B9781A", 0.35)}`,
                },
              }}
            >
              <MessageIcon sx={{ fontSize: "0.9rem" }} />
              <Typography variant="caption" fontWeight={700} sx={{ fontSize: "0.75rem" }}>
                {t("business.inquire")}
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Comment input — only for signed-in users */}
      {user && (
      <Paper elevation={0} sx={{ border: `1px solid ${alpha(NAVY, 0.1)}`, borderRadius: 3, p: 2, mb: 3, backgroundImage: CARD_TINT_GRADIENT, backgroundColor: "#FFFFFF" }}>
        <Typography variant="subtitle2" fontWeight={700} mb={1}>{t("common.comment")}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Avatar sx={{ background: "linear-gradient(135deg, #1a3a7a 0%, #0F2156 100%)", width: 32, height: 32, fontSize: "0.8rem", fontWeight: 700 }}>
            {user?.first_name?.charAt(0) ?? "?"}
          </Avatar>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={1}
            maxRows={4}
            placeholder={t("common.comment") + "..."}
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
              // 16px prevents iOS Safari from auto-zooming the page on focus
              "& .MuiOutlinedInput-input": { fontSize: "16px" },
            }}
          />
          <Button
            variant="contained"
            size="small"
            disabled={submitting || !commentText.trim()}
            onClick={handleComment}
            sx={{ borderRadius: 2, alignSelf: "flex-end", fontWeight: 600, whiteSpace: "nowrap", color: "white", "&.Mui-disabled": { color: "rgba(255,255,255,0.7)", bgcolor: "rgba(0,0,0,0.25)" } }}
          >
            {t("common.post")}
          </Button>
        </Box>
      </Paper>
      )}

      {/* Comments list */}
      <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
        {t("common.comment")} ({comments.length})
      </Typography>
      {comments.length === 0 && (
        <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
          {t("feed.noCommentsYet")}
        </Typography>
      )}
      {[...comments].reverse().map((c) => (
        <Box key={c.id} sx={{ display: "flex", gap: 1.5, mb: 2 }}>
          <Avatar
            sx={{ background: c.user ? "linear-gradient(135deg, #1a3a7a 0%, #0F2156 100%)" : "#9CA3AF", width: 36, height: 36, fontSize: "0.85rem", fontWeight: 700, cursor: c.user ? "pointer" : "default" }}
            onClick={() => { if (c.user) navigate(`/app/profile/${c.user.username}`); }}
          >
            {c.user?.first_name?.charAt(0) ?? "?"}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ bgcolor: "#F3F4F6", borderRadius: 2, px: 1.5, py: 1 }}>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{ cursor: c.user ? "pointer" : "default", "&:hover": c.user ? { color: CRIMSON } : {} }}
                onClick={() => { if (c.user) navigate(`/app/profile/${c.user.username}`); }}
              >
                {c.user ? `${c.user.first_name} ${c.user.last_name}` : t("common.anonymous")}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.3, whiteSpace: "pre-wrap" }}>{c.content}</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {new Date(c.created_at).toLocaleString()}
            </Typography>
          </Box>
        </Box>
      ))}
    </Container>

    {/* Inquire Dialog */}
    <Dialog open={inquireOpen} onClose={() => setInquireOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>{t("business.sendInquiry")}</DialogTitle>
      <DialogContent>
        {inquireError && (
          <Alert severity="error" sx={{ mt: 1 }}>{inquireError}</Alert>
        )}
        {post.is_anonymous && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            {t("business.inquiryToAnonymous")}
          </Typography>
        )}
        <TextField
          label={t("business.inquiryMessage")}
          fullWidth
          margin="normal"
          multiline
          rows={4}
          value={inquireMsg}
          onChange={(e) => setInquireMsg(e.target.value)}
          placeholder={t("business.inquiryPlaceholder")}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => setInquireOpen(false)}>{t("common.cancel")}</Button>
        <Button
          variant="contained"
          onClick={handleInquire}
          disabled={inquireLoading || !inquireMsg.trim()}
          sx={{
            color: "white",
            "&.Mui-disabled": { color: "rgba(255,255,255,0.7)", bgcolor: "rgba(0,0,0,0.25)" },
          }}
        >
          {inquireLoading ? t("common.sending") : t("common.send")}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}
